// Plan-starter -> Logger draft hydration. Pure (no React, no component
// state). Split out of workoutMapping.ts (Track R) — distinct seam from
// saved-workout hydration: the input is a PlanStarter (prescribed sets),
// not a performed Workout, so completion/notes/rpe semantics differ
// (everything starts blank, columns seed from the prescription).
import {
  catalogBySlug,
  fromCanonicalKg,
  isEffortDerivedWeightSource,
  plateLoadModeFor,
  roundLoadable,
  type Exercise,
  type LoadableContext,
  type MovementPattern,
  type PlanStarter,
} from '@heraion/shared';
import type { PlateLoadShape } from './state/plateConfig';
import { resolveLoadShape } from './state/resolveLoadShape';
import { canonicalRirFromNumbers } from './state/canonicalRir';
import type {
  DraftEntry,
  LoggerDraft,
  WeightUnit,
} from './state/loggerDraftTypes';
import { catalogLoadedSides, deriveEntryKind } from './deriveEntryKind';
import { bornExtraNegative } from './state/loggerDraftHelpers';

export interface PlanStarterHydrationOptions {
  catalogPatterns?: Record<string, MovementPattern | null>;
  // Finest loadable increment for the athlete's plates, in BOTH units
  // (device-local, from plateConfig.loadRoundingContext). Only the
  // %1RM-derived seed is floored to it (LG-15); a row floors on ITS OWN
  // unit's step (ctx.stepKg or ctx.stepLb) - omit: roundLoadable's
  // per-unit default.
  loadableContext?: LoadableContext;
  // Silent machine per slug (EQ-1), injected so this stays pure. Omitted: null.
  resolveInstance?: (slug: string) => string | null;
  // The load shape the athlete saved per slug (plateConfig.loadPlateLoadShape),
  // injected so this stays pure. A slug without one reads the catalog default.
  loadShapes?: ReadonlyMap<string, PlateLoadShape>;
}

// Builds a logger draft from a plan starter (buildPlanStarter output).
// Mirrors hydrateFromWorkout: weighted sets get the resolved kg as
// the visible weight, bodyweight entries surface the prescribed extra
// (signed). The plan's fromPlanId/name/notes pass through unchanged so
// the save payload carries them to the API.
//
// catalog provides the isBodyweight flag per slug. When the catalog is
// missing the slug (rare — drafts created against an out-of-date
// snapshot), we fall back to weighted; the user can still log the
// session, the worst case is a misrendered set-row label.
export function hydrateFromPlanStarter(
  starter: PlanStarter,
  unit: WeightUnit,
  catalog: ReadonlyArray<Exercise>,
  {
    catalogPatterns = {},
    loadableContext,
    resolveInstance,
    loadShapes,
  }: PlanStarterHydrationOptions = {},
): LoggerDraft {
  const bySlug = catalogBySlug(catalog);

  // The coach's prescribed effort, as CANONICAL RIR. The program authors in
  // one scale (rpe OR rir); this normalises to rir and stops there — the
  // athlete's scale is a render-time projection, never a stored mirror.
  // ⛔ It used to pre-fill `rpe`/`rir` on the draft set, i.e. the MEASUREMENT
  // fields, which is how a Fail or a self-closing bout saved the target as
  // the judgment (prescribedEffortLeak.test.ts). It lands on the
  // prescription half now, like amrapPrescribed and workPrescribed.
  const prescribedRir = (s: { rpe?: number; rir?: number }): number | undefined =>
    canonicalRirFromNumbers(s.rir, s.rpe) ?? undefined;

  const entries: DraftEntry[] = starter.entries.map((e) => {
    const ex = bySlug.get(e.exerciseId);
    // Same catalog→kind rule as add + swap (deriveEntryKind), but the plan
    // starter may hit a missing catalog row, so it pre-resolves movementPattern
    // with its own fallback and passes the parts.
    const { isBodyweight, isAssisted, isCardio, isTimeBased, movementPattern } =
      deriveEntryKind({
        isBodyweight: ex?.isBodyweight,
        assisted: ex?.assisted,
        movementPattern:
          ex?.movementPattern ?? catalogPatterns[e.exerciseId] ?? null,
        isTimeBased: ex?.isTimeBased,
      });
    // Laterality multiplier seeded from the catalog via the shared seam
    // (catalogLoadedSides — same rule as add + swap, incl. the cardio
    // guard this copy used to lack). The entry-header chip overrides.
    // Kept on one variable so the entry and its weighted sets stay
    // consistent.
    const lateralSides: 1 | 2 = catalogLoadedSides({
      movementPattern: ex?.movementPattern ?? catalogPatterns[e.exerciseId] ?? null,
      unilateral: ex?.unilateral,
    });
    // Plan-driven effort column: ON iff the coach prescribed effort
    // somewhere on this entry — per-set rpe/rir target OR per-entry
    // rpeEnabled/rirEnabled opt-in flag. Cardio entries never get the
    // column. No prescription = column off; the athlete opts in per
    // entry via the chip. Decoupled from prefill (effortStrings above):
    // an opt-in flag with no per-set target yields a visible-but-empty
    // column. Replaces the v2.39.0 default-on-for-everything rule that
    // ignored Slot.rirEnabled/rpeEnabled (inbox dd7de4f8, 2026-05-28).
    const hasPerSetEffortTarget = e.sets.some(
      (s) => s.rpe !== undefined || s.rir !== undefined,
    );
    const effortColumn =
      !isCardio &&
      !isTimeBased &&
      (e.rpeEnabled === true ||
        e.rirEnabled === true ||
        hasPerSetEffortTarget);
    return {
      exerciseId: e.exerciseId,
      // DraftEntry.exerciseName is a snapshot string; resolve to EN
      // canonical here so the draft serialises predictably. The UI
      // re-localises live wherever it renders from a live catalog row.
      exerciseName: ex?.name.en ?? e.exerciseId,
      isBodyweight,
      isAssisted,
      isCardio,
      isTimeBased,
      movementPattern,
      group: e.group ?? null,
      // EQ-1: a plan prescribes the EXERCISE, never the machine. The athlete's
      // history picks it, same silent rule as add/replace (resolveInstance).
      equipmentInstance: resolveInstance?.(e.exerciseId) ?? null,
      location: null,
      loadedSides: lateralSides,
      // Plan cues (💬 instruction + 🎯 per-set) are NOT copied into the draft
      // (note-model consolidation): they're display-only, re-resolved per-slug
      // by usePlanCues, so an in-workout swap can't inherit the old exercise's.
      effortColumn,
      // #353: % of a MAX they do not have. The cells stay blank; the card
      // offers the calibration.
      ...(e.calibrate ? { calibration: 'offered' as const } : {}),
      historyOpen: false,
      sets: e.sets.map((s) => {
        // The plan's AMRAP lands on the PRESCRIPTION half only. It draws
        // the read-only badge; it must never be mistaken for what the
        // athlete actually did, so the outcome half starts false on every
        // freshly started set and stays false until the athlete says
        // otherwise with the chip (measurement-contract §5: what you
        // declare serves to propose, never to measure).
        const amrapPrescribed = s.amrap === true;
        // ...and on an AMRAP the reps CELL is the prescription's last way
        // in, so it closes here (LG-28). Every ordinary set seeds its
        // prescribed reps into the cell, which is a helpful default: the
        // plan asked for 10, 10 is almost certainly what you did. On an
        // AMRAP the same seed is a trap — the number in the box is the
        // floor, and an athlete who reads it as the answer stops there.
        // That is exactly what happened in the smoke of 2026-07-24: sets
        // prescribed AMRAP, performed at the prescribed reps, then
        // confirmed "to failure" — a measurement fabricated out of a
        // request ([[feedback_prescription_never_becomes_measurement]]).
        // Empty cell + floor in the placeholder: the athlete must type
        // what they actually did, and there is nothing to accept by
        // doing nothing.
        // AP-9b: a built draft carries 0 reps for an exercise never logged
        // (D2). Empty, not «0»: there is no last time to propose.
        const noRepsToPropose =
          starter.todayProposal !== undefined && s.reps === 0;
        const reps = amrapPrescribed || noRepsToPropose ? '' : String(s.reps);
        const amrapFloorField = amrapPrescribed
          ? {
              amrapFloor: String(s.reps),
              ...(s.amrapStop ? { amrapStopPrescribed: s.amrapStop } : {}),
            }
          : {};
        // #336 S3: the range sits beside the prefilled target, display only.
        const rangeField =
          s.repsMin !== undefined && s.repsMax !== undefined && !amrapPrescribed
            ? { repsRangePrescribed: { min: s.repsMin, max: s.repsMax } }
            : {};
        // Omit-when-absent, so a set no plan prescribed an effort for has a
        // draft shape byte-identical to what it was before.
        const effortPrescribedField = (() => {
          // Load and effort are EXCLUSIVE axes (PE-22.6 §1). The Program
          // resolver guarantees it by construction — an effort intensity emits
          // targetWeight:none — but a PLAN does not: PlannedSet is a flat object
          // with no cross-field refine, and the legacy web let a coach author
          // "100 kg × 5 @ RPE 8". Started as-is, that set would draw a target
          // ghost on a set whose axis is the WEIGHT.
          // A prescribed weight therefore wins: the effort there is a cap to
          // MEASURE, not a target to draw. ⛔ The gate is only about the
          // prescription half — `hasPerSetEffortTarget` above still sees s.rpe,
          // so the effort COLUMN stays and the athlete records it as always.
          // 'history'/absent stay eligible: neither is a coach-prescribed load.
          const weightIsTheAxis =
            s.weightSource === 'prescribed-absolute' ||
            s.weightSource === 'computed-percent';
          const target = weightIsTheAxis ? undefined : prescribedRir(s);
          return target === undefined ? {} : { rirPrescribed: target };
        })();
        // weightSource gates the prefilled cell (Phase 1, default-weight
        // chain). Absent → 'blank' from resolveDefaultWeight → empty
        // cell; otherwise stamp the chain's source onto the DraftSet so
        // the save path can persist provenance + an athlete edit can
        // flip to 'manual'. Bodyweight keeps its own pure-bw render gate
        // (kg === 0) since "no prescription" on a bodyweight exercise
        // means a pure-bw rep, not a blank cell.
        const sourceField = s.weightSource
          ? { weightSource: s.weightSource }
          : {};
        // TIME AXIS (HI-1 slice 2). The plan's work bound and per-set rest
        // land on the PRESCRIPTION half, like amrapPrescribed above: they
        // say what to do, never what was done. Both omit-when-absent so a
        // reps set's draft shape is byte-identical to what it was before.
        const timeAxisFields = {
          ...(s.work !== undefined ? { workPrescribed: s.work } : {}),
          ...(s.restSec !== undefined ? { restPrescribedSec: s.restSec } : {}),
        };
        // One literal, three weight shapes. It used to be three near-identical
        // literals, and the fields that vary between them are exactly three
        // (weight, extraNegative, and nothing else) — every other key was
        // copied verbatim, so a field added to the set shape had to be typed
        // three times or it would land on two kinds out of three. HI-1 was the
        // fourth such field: it collapses here instead (§CONSOLIDAMENTO).
        const base = {
          reps,
          unit,
          completedAt: null,
          fail: false,
          prFlash: false,
          note: '',
          // ⛔ The MEASUREMENT half starts empty on a plan-started set. The
          // coach's target lives in rirPrescribed below; writing it here is
          // what made a Fail save "I had three left" on a set the athlete
          // could not finish.
          rpe: '',
          rir: '',
          loadedSides: lateralSides,
          duration: '',
          distanceM: '',
          avgHrBpm: '',
          calories: '',
          amrapPrescribed,
          ...effortPrescribedField,
          ...amrapFloorField,
          ...rangeField,
          ...timeAxisFields,
          amrap: false,
          dropset: null,
          ...sourceField,
        };
        if (isBodyweight) {
          // Empty extra means "pure bw". Plan-prescribed 0 also reads
          // as "pure bw" in the logger — the athlete can add load with
          // the per-row chip if the plan was wrong, no surprise.
          // #213: a prescribed or historic extra keeps its sign (+10 stays
          // positive, -20 negative); with none, the catalog flag decides.
          const weight =
            s.weightKg === 0 ? '' : String(fromCanonicalKg(Math.abs(s.weightKg), unit));
          return {
            ...base,
            weight,
            extraNegative: bornExtraNegative(
              { isAssisted },
              { weight, extraNegative: s.weightKg < 0 },
            ),
          };
        }
        // DERIVED seeds resolve to un-loadable numbers (e.g. 68.38 kg, LG-15)
        // — floor to the athlete's finest plate increment so the seeded cell
        // shows a weight they can build. Display/seed only; the plan's saved
        // target is untouched. The remaining sources (prescribed absolute,
        // history, manual) already came from a real scale or a real entry.
        //
        // ⭐ The effort-derived loads (PG-9) join the %-of-MAX ones here for
        // the identical reason — dividing an e1RM by (1 + reps/30) lands on
        // 82.37 just as readily as a percentage does, and "carica la barra e
        // fai un RIR 2" is worth nothing if the bar cannot hold the number.
        const seededKg =
          s.weightSource === 'computed-percent' ||
          isEffortDerivedWeightSource(s.weightSource)
            ? // ⭐ CN-39 — la forma di carico dell'esercizio, non `'pairs'` per
              // tutti. Su un landmine un disco da 1,25 muove 1,25, non 2,5, e
              // prima il seed floorava sulla scaletta sbagliata mentre il «≈»
              // usava quella giusta: due proposte dalla stessa app.
              // CN-45: vince la forma che l'atleta ha salvato per quello slug
              // (iniettata in `loadShapes`), poi il default di CATALOGO.
              roundLoadable(
                s.weightKg,
                unit,
                loadableContext,
                plateLoadModeFor(
                  resolveLoadShape(loadShapes?.get(e.exerciseId), ex?.barLoading),
                ),
              )
            : s.weightKg;
        return {
          ...base,
          weight:
            s.weightSource !== undefined
              ? String(fromCanonicalKg(seededKg, unit))
              : '',
          extraNegative: false,
        };
      }),
    };
  });

  // The draft holds an ISO datetime taken from the device clock at
  // hydration, so the canonical timestamp is honest about when the
  // session actually starts.
  const startedAt = new Date().toISOString();

  return {
    startedAt,
    entries,
    ...(starter.planId ? { fromPlanId: starter.planId } : {}),
    ...(starter.name ? { name: starter.name } : {}),
    ...(starter.notes ? { notes: starter.notes } : {}),
    ...(starter.coachNotes ? { coachNotes: starter.coachNotes } : {}),
    ...(starter.todayProposal ? { todayProposal: starter.todayProposal } : {}),
    ...(starter.roundType ? { roundType: starter.roundType } : {}),
  };
}
